import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Shield,
  FileText,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Moon,
  Sun,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { Tooltip } from '@/components/ui/Tooltip';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
}

const adminNavItems: NavItem[] = [
  { label: 'Overview', icon: <LayoutDashboard className="w-5 h-5" />, href: '/admin' },
  { label: 'Users', icon: <Users className="w-5 h-5" />, href: '/admin/users' },
  { label: 'Projects', icon: <FolderKanban className="w-5 h-5" />, href: '/admin/projects' },
  { label: 'Certificates', icon: <Shield className="w-5 h-5" />, href: '/admin/certificates' },
  { label: 'Audit Logs', icon: <FileText className="w-5 h-5" />, href: '/admin/logs' },
];

export function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const isActive = (href: string) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  const NavLinkItem = ({ item }: { item: NavItem }) => {
    const active = isActive(item.href);

    const content = (
      <NavLink
        to={item.href}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
          'hover:bg-gray-100 dark:hover:bg-dark-700',
          active
            ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
            : 'text-gray-700 dark:text-gray-300'
        )}
      >
        {item.icon}
        {!collapsed && (
          <span className="font-medium">{item.label}</span>
        )}
      </NavLink>
    );

    if (collapsed) {
      return <Tooltip content={item.label} position="right">{content}</Tooltip>;
    }
    return content;
  };

  return (
    <aside
      className={cn(
        'flex flex-col h-screen bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-dark-700 transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-dark-700">
        <NavLink to="/admin" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-gray-900 dark:text-white">Spark</span>
              <span className="text-xs text-red-500 font-medium -mt-1">Admin</span>
            </div>
          )}
        </NavLink>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-dark-700 transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {adminNavItems.map((item) => (
          <NavLinkItem key={item.href} item={item} />
        ))}
      </nav>

      {/* Bottom Navigation */}
      <div className="px-3 py-4 space-y-1 border-t border-gray-200 dark:border-dark-700">
        {/* Back to App */}
        <NavLink
          to="/dashboard"
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
            'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'
          )}
        >
          <ArrowLeft className="w-5 h-5" />
          {!collapsed && <span className="font-medium">Back to App</span>}
        </NavLink>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
            'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'
          )}
        >
          {theme === 'dark' ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
          {!collapsed && (
            <span className="font-medium">
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}
